import type { EmployeeRuntimeActor } from "../gateway/identity-client";
import { posRuntimeDb } from "./pos-db";

const employeeSessionKey = "employee-session:current";
export const employeeSessionClearedEventName = "more-pos:employee-session-cleared";

export type LocalEmployeeSessionState = {
  sessionToken: string;
  actor: EmployeeRuntimeActor;
  source: "CORE" | "LOCAL";
  verifiedAt: string | null;
};

function scopeKey(actor: EmployeeRuntimeActor) {
  return `org:${actor.organization_id}:branch:${actor.branch_id}:node:${actor.node_id}:employee:${actor.employee_id}`;
}

export async function readLocalEmployeeSession(): Promise<LocalEmployeeSessionState | null> {
  try {
    const record = await posRuntimeDb.runtimeMeta.get(employeeSessionKey);
    if (!record || !record.value || typeof record.value !== "object") return null;

    const value = record.value as Partial<LocalEmployeeSessionState>;
    const actor = value.actor as EmployeeRuntimeActor | undefined;
    if (
      typeof value.sessionToken !== "string" ||
      !value.sessionToken ||
      !actor?.runtime_session_id ||
      !actor.employee_id ||
      !actor.node_id ||
      !actor.branch_id ||
      !actor.organization_id
    ) {
      return null;
    }

    return {
      sessionToken: value.sessionToken,
      actor,
      source: value.source === "CORE" ? "CORE" : "LOCAL",
      verifiedAt: typeof value.verifiedAt === "string" ? value.verifiedAt : null,
    };
  } catch {
    // Employee recovery is secondary to Terminal recovery. A malformed/stale
    // employee cache must never make a trusted terminal look unactivated.
    return null;
  }
}

export async function saveCoreEmployeeSession(
  sessionToken: string,
  actor: EmployeeRuntimeActor,
) {
  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: employeeSessionKey,
    scopeKey: scopeKey(actor),
    value: {
      sessionToken,
      actor,
      source: "CORE",
      verifiedAt: now,
    } satisfies LocalEmployeeSessionState,
    updatedAt: now,
  });
}

export async function markEmployeeSessionLocal(
  state: LocalEmployeeSessionState,
) {
  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: employeeSessionKey,
    scopeKey: scopeKey(state.actor),
    value: {
      ...state,
      source: "LOCAL",
    } satisfies LocalEmployeeSessionState,
    updatedAt: now,
  });
}

export async function clearLocalEmployeeSession() {
  await posRuntimeDb.runtimeMeta.delete(employeeSessionKey);

  // Local session deletion is a lifecycle signal, not proof that Access Core ended the
  // canonical session. Bootstrap listens for this so an accidental/stale local invalidation
  // can heal from the already-active Core session. Explicit Logout remains safe because it
  // ends the Core session before clearing Local, so the recovery contract fails closed.
  window.dispatchEvent(new CustomEvent(employeeSessionClearedEventName));
}
